import {
  useApiClient,
  WebshopShoppingCartCommands,
  WebshopShoppingCartEvent,
  WebshopShoppingCartQueries,
  WebshopShoppingCartVersioned,
  webshopShoppingCartUpdateProductQuantityService,
} from '@cldsystems/ws-api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { MyCartActionButtonsEnum } from '@/types/action-buttons/action-buttons';
import { UpdateMutateProps } from '@/types/common';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { WithQuantity } from '@/types/products/with-quantity';
import {
  getApiErrorMessage,
  isNoChangesError,
  isOptimisticLockError,
} from '@/utils/get-api-error-message';
import { priceCheck } from '@/utils/price-check';

type ShoppingCartUpdateProductQuantityPayload = {
  productId: string;
  quantity: number;
  event: WebshopShoppingCartEvent;
  product: WithQuantity<SimpleProductInfo>;
  operationId: string;
  buttonType: MyCartActionButtonsEnum;
  optimisticApplied?: boolean;
  previousQuantity: number;
  /** Skip the error toast for a (retryable) optimistic-lock conflict. */
  suppressVersionConflictToast?: boolean;
};

type ShoppingCartUpdateProductQuantityContext = {
  shoppingCartId: string;
  productId: string;
  product: WithQuantity<SimpleProductInfo>;
  operationId: string;
  previousQuantity: number;
  previousVersion?: number;
};

const useShoppingCartUpdateProductQuantityMutate = (
  markMyCartPending: (
    productId: string,
    buttonType: MyCartActionButtonsEnum | undefined,
    operationId: string,
  ) => void,
  clearMyCartPending: (productId: string, operationId?: string) => void,
  getMyCartPending: (productId: string) =>
    | {
        operationId: string;
      }
    | undefined,
  setIsPendingUpdateMyCartProductId: (id: string | undefined) => void,
  setActionButtonType: (buttonType: MyCartActionButtonsEnum | undefined) => void,
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { httpClient, hasSchema } = useApiClient();

  return useMutation({
    mutationKey: [WebshopShoppingCartCommands.UpdateProductQuantity],
    mutationFn: ({
      params,
      payload,
    }: UpdateMutateProps<ShoppingCartUpdateProductQuantityPayload>) =>
      webshopShoppingCartUpdateProductQuantityService({
        httpClient,
        hasSchema: Boolean(hasSchema),
      })({
        params,
        payload: {
          productId: payload!.productId,
          quantity: payload!.quantity,
          event: payload!.event,
        },
      }),
    onMutate: async ({ params, payload }) => {
      if (!payload) return;

      markMyCartPending(payload.productId, payload.buttonType, payload.operationId);
      setIsPendingUpdateMyCartProductId(payload.productId);
      setActionButtonType(payload.buttonType);

      const queryKey = [WebshopShoppingCartQueries.GetById, params.id];
      await queryClient.cancelQueries({ queryKey });
      const previousShoppingCart = queryClient.getQueryData<WebshopShoppingCartVersioned>(queryKey);

      queryClient.setQueryData<WebshopShoppingCartVersioned>(queryKey, old => {
        if (!old) return undefined;

        const foundProduct = old.shoppingCart.products.find(p => p.id === payload.productId);
        const isProductExists = !!foundProduct;

        if (!isProductExists) {
          return old;
        }

        const products = old.shoppingCart.products.map(p =>
          p.id === payload.productId
            ? {
                ...p,
                quantity: payload.optimisticApplied ? p.quantity : payload.quantity,
              }
            : p,
        );
        const newAmount = products.reduce(
          (acc, current) => acc + current.price * current.quantity,
          0,
        );
        const newDiscountedAmount = products.reduce(
          (acc, current) => acc + current.discountedPrice * current.quantity,
          0,
        );
        const newTotalAmount = newDiscountedAmount;

        return {
          ...old,
          version: old.version + 1,
          shoppingCart: {
            ...old.shoppingCart,
            amount: priceCheck(newAmount),
            discountedAmount: priceCheck(newDiscountedAmount),
            totalAmount: priceCheck(newTotalAmount),
            products,
          },
        };
      });

      return {
        shoppingCartId: params.id,
        productId: payload.productId,
        product: payload.product,
        operationId: payload.operationId,
        previousQuantity: payload.previousQuantity,
        previousVersion: previousShoppingCart?.version,
      } satisfies ShoppingCartUpdateProductQuantityContext;
    },
    onError: (err, variables, context) => {
      if (context) {
        const currentPending = getMyCartPending(context.productId);
        const queryKey = [WebshopShoppingCartQueries.GetById, context.shoppingCartId];
        queryClient.setQueryData<WebshopShoppingCartVersioned>(queryKey, old => {
          if (!old) {
            return old;
          }

          const shouldRollbackQuantity =
            !currentPending || currentPending.operationId === context.operationId;
          const products = shouldRollbackQuantity
            ? old.shoppingCart.products.map(product =>
                product.id === context.productId
                  ? { ...product, quantity: context.previousQuantity }
                  : product,
              )
            : old.shoppingCart.products;
          const newAmount = products.reduce(
            (acc, current) => acc + current.price * current.quantity,
            0,
          );
          const newDiscountedAmount = products.reduce(
            (acc, current) => acc + current.discountedPrice * current.quantity,
            0,
          );

          return {
            ...old,
            version: context.previousVersion ?? old.version,
            shoppingCart: {
              ...old.shoppingCart,
              amount: priceCheck(newAmount),
              discountedAmount: priceCheck(newDiscountedAmount),
              totalAmount: priceCheck(newDiscountedAmount),
              products,
            },
          };
        });
      }
      if (context) {
        clearMyCartPending(context.productId, context.operationId);
      }
      setIsPendingUpdateMyCartProductId(undefined);
      setActionButtonType(undefined);

      // No-op commands ("no changes detected") are harmless. Optimistic-lock
      // conflicts are retried with the real version by the caller; stay silent
      // on the first failure so we only notify if the retry also fails.
      if (
        isNoChangesError(err) ||
        (variables.payload?.suppressVersionConflictToast && isOptimisticLockError(err))
      ) {
        return;
      }
      toast.error(getApiErrorMessage(err, t?.common.productUpdateQuantityFailed));
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context) {
        clearMyCartPending(context.productId, context.operationId);
      }
      setIsPendingUpdateMyCartProductId(undefined);
      setActionButtonType(undefined);
    },
  });
};

export default useShoppingCartUpdateProductQuantityMutate;
